import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

// Saved with the app's contact messages: the admin sees it in « Contact Queries ».
const API = "https://omni-cave.com/backend/api/v1/auth/landing-contact";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_MESSAGE = 2000;

const empty = { name: "", email: "", message: "" };

const validate = ({ name, email, message }) => {
  const errors = {};
  if (name.trim().length < 2) errors.name = "Veuillez indiquer votre nom.";
  if (!EMAIL_RE.test(email.trim())) errors.email = "Veuillez indiquer une adresse e-mail valide.";
  if (message.trim().length < 10) errors.message = "Votre message doit contenir au moins 10 caractères.";
  return errors;
};

/** « Nous contacter » page with the contact form. */
export default function Contact() {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const [serverError, setServerError] = useState("");
  // Anti-spam: the server refuses a form sent within 3 s of opening (bots).
  const openedAt = useRef(Date.now());

  useEffect(() => {
    document.title = "Nous contacter | OmniCave";
    return () => {
      document.title = "OmniCave";
    };
  }, []);

  const set = (key) => (e) => {
    const value = key === "message" ? e.target.value.slice(0, MAX_MESSAGE) : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: "" }));
    setServerError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    setStatus("sending");
    setServerError("");
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          form_open_ms: Date.now() - openedAt.current,
        }),
      }).then((r) => r.json());
      if (res?.success) {
        setStatus("sent");
        setForm(empty);
      } else {
        setStatus("idle");
        setServerError(res?.message || "Le message n'a pas pu être envoyé. Merci de réessayer.");
      }
    } catch (err) {
      setStatus("idle");
      setServerError("Le message n'a pas pu être envoyé. Vérifiez votre connexion et réessayez.");
    }
  };

  return (
    <section className="legalPage contactPage">
      <div className="container">
        <nav className="legalPage__crumbs">
          <Link to="/">Accueil</Link>
          <span>/</span>
          <span>Nous contacter</span>
        </nav>
        <h1>Nous contacter</h1>
        <p className="contactPage__intro">
          Une question sur OmniCave, une suggestion ou un souci avec votre compte ? Écrivez-nous,
          nous vous répondrons par e-mail.
        </p>

        {status === "sent" ? (
          <div className="contactPage__sent" role="status">
            <strong>Merci, votre message a bien été envoyé.</strong>
            <p>Nous vous répondrons rapidement à l'adresse indiquée.</p>
            <button
            type="button"
            className="contactPage__again"
            onClick={() => {
              openedAt.current = Date.now();
              setStatus("idle");
            }}
          >
              Envoyer un autre message
            </button>
          </div>
        ) : (
          <form className="contactPage__form" onSubmit={submit} noValidate>
            <div className="contactPage__row">
              <label className="contactPage__field">
                <span>Nom</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  maxLength={100}
                  autoComplete="name"
                  aria-invalid={!!errors.name}
                />
                {errors.name && <em>{errors.name}</em>}
              </label>
              <label className="contactPage__field">
                <span>E-mail</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  maxLength={200}
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                />
                {errors.email && <em>{errors.email}</em>}
              </label>
            </div>
            <label className="contactPage__field">
              <span>Message</span>
              <textarea
                rows={7}
                value={form.message}
                onChange={set("message")}
                aria-invalid={!!errors.message}
              />
              <small>
                {form.message.length} / {MAX_MESSAGE}
              </small>
              {errors.message && <em>{errors.message}</em>}
            </label>
            {serverError && <div className="contactPage__error">{serverError}</div>}
            <button type="submit" className="contactPage__submit" disabled={status === "sending"}>
              {status === "sending" ? "Envoi…" : "Envoyer le message"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
